import { createSlice } from "@reduxjs/toolkit";
import {
  EMPTY_STRING,
  DEFAULT_LIMIT,
  ZERO,
  ONE,
  BACKEND_FAILURE_STATUS,
  FRONTEND_FAILURE_STATUS,
} from "../../constants";
import {
  fetchApplications,
  fetchApplicationById,
  updateApplication,
  createApplication,
  deleteApplication,
  fetchConstantsFromLibrary,
  fetchApplicationDeploymentStatus,
  fetchApplicationsDeploymentsTrack,
} from "./applications.actions";

const paginationFilters = {
  name: EMPTY_STRING,
  page: ONE,
  limit: DEFAULT_LIMIT,
};

const slice = createSlice({
  name: "applications",
  initialState: {
    applications: {
      data: [],
    },
    selectedApp: null,
    selectedAppId: null,
    shouldShowSidePanel: false,
    shouldShowMenuBar: false,
    selectedModal: null,
    totalApplications: ZERO,
    applicationFilters: paginationFilters,
    shouldShowLeftPane: true,
    appDeploymentStatus: { frontend: null, backend: null },
    recentlySelectedApplication: null,
    appPipelineStatus: null,
    appDeploymentsTrack: { deploymentStatus: [], count: null },
  },
  reducers: {
    setSelectedModal(state, action) {
      state.selectedModal = action.payload;
    },
    setSelectedApp(state, action) {
      state.selectedApp = action.payload;
    },
    setSelectedAppId(state, action) {
      state.selectedAppId = action.payload;
    },
    setShouldShowSidePanel(state, action) {
      state.shouldShowSidePanel = action.payload;
    },
    setShouldShowMenuBar(state, action) {
      state.shouldShowMenuBar = action.payload;
    },
    setApplicationFilters(state, action) {
      state.applicationFilters = action.payload || paginationFilters;
    },
    setShouldShowLeftPane(state, action) {
      state.shouldShowLeftPane = action.payload;
    },
    setAppDeployStatus(state, action) {
      state.appDeploymentStatus = action.payload;
    },
    setRecentlySelectedApp(state, action) {
      state.recentlySelectedApplication = action.payload;
    },
    setAppPipelineStatus(state, action) {
      state.appPipelineStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.applications = action.payload || state.applications;
        state.totalApplications = action.payload.total;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedApplications = state.applications.data.map((application) =>
          application.id === action.payload.id
            ? { ...application, ...action.payload }
            : application
        );
        state.applications = {
          ...state.applications,
          data: updatedApplications,
        };
        state.selectedApp = action.payload;
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.selectedApp = action.payload || state.selectedApp;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.applications = {
          ...state.applications,
          total: state.applications.total + ONE,
          data: [action.payload, ...state.applications.data],
        };
        state.totalApplications = state.totalApplications + ONE;
        state.applicationFilters = { ...state.applicationFilters, page: ONE };
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedApplications = state.applications.data.filter(
          (item) => item.id !== action.payload.id
        );
        state.applications = {
          ...state.applications,
          total: state.applications.total - ONE,
          data: updatedApplications,
        };
        state.totalApplications = state.totalApplications - ONE;
        const targetPage =
          state.totalApplications % DEFAULT_LIMIT === ZERO
            ? state.applicationFilters.page - ONE
            : state.applicationFilters.page;
        state.applicationFilters = {
          ...state.applicationFilters,
          page: targetPage,
        };
      })
      .addCase(fetchConstantsFromLibrary.fulfilled, (state, action) => {
        if (!action.payload) return;
        return { ...state, ...action.payload };
      })
      .addCase(fetchApplicationDeploymentStatus.fulfilled, (state, action) => {
        if (!action.payload) return;
        const isPipelineStatusFailed =
          action.payload.frontend?.message ||
          action.payload.backend?.message ||
          action.payload?.message;
        if (isPipelineStatusFailed) {
          const frontend =
            action.payload.frontend?.message || action.payload.message
              ? {
                  ...action.payload.frontend,
                  ...FRONTEND_FAILURE_STATUS,
                }
              : action.payload.frontend;
          const backend =
            action.payload.backend?.message || action.payload.message
              ? {
                  ...action.payload.backend,
                  ...BACKEND_FAILURE_STATUS,
                }
              : action.payload.backend;
          state.appPipelineStatus = { backend, frontend };
        } else state.appPipelineStatus = action.payload;
      })
      .addCase(fetchApplicationsDeploymentsTrack.fulfilled, (state, action) => {
        if (action.payload) state.appDeploymentsTrack = action.payload;
      });
  },
});

export default slice;

export const { name, actions, reducer } = slice;
